import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Input, InputNumber, List, Select, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import { useSleepManagement } from '../hooks/useSleepManagement'

const { Title, Text } = Typography
const { Option } = Select

const SleepManagement = ({ sleepManagement, setSleepManagement }) => {
  const [form] = Form.useForm()
  const [selectedSleep, setSelectedSleep] = useState(null)
  const { sleepOptions, isLoading } = useSleepManagement()

  useEffect(() => {
    form.resetFields()
  }, [selectedSleep, form])

  const handleAddSleep = (values) => {
    const newSleep = {
      id: Date.now(),
      name: values.name,
      type: values.type,
      description: values.description,
      duration: values.duration,
      frequency: values.frequency,
      goals: values.goals,
      recommendations: values.recommendations
    }

    setSleepManagement([...sleepManagement, newSleep])
    form.resetFields()
  }

  const handleDeleteSleep = (id) => {
    setSleepManagement(sleepManagement.filter((item) => item.id !== id))
    if (selectedSleep && selectedSleep.id === id) {
      setSelectedSleep(null)
    }
  }

  const handleEditSleep = () => {
    if (!selectedSleep) return

    const values = form.getFieldsValue()
    const updatedSleep = sleepManagement.map((item) => {
      if (item.id === selectedSleep.id) {
        return {
          ...item,
          name: values.name,
          type: values.type,
          description: values.description,
          duration: values.duration,
          frequency: values.frequency,
          goals: values.goals,
          recommendations: values.recommendations
        }
      }
      return item
    })

    setSleepManagement(updatedSleep)
    setSelectedSleep(null)
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Title level={4}>Sleep & Recovery Tracking</Title>
        <Text>Configure sleep hygiene practices, recovery techniques, and rest schedules for the wellness plan.</Text>
      </Col>

      <Col span={12} style={{ paddingRight: 12 }}>
        <Card title='Sleep & Recovery Plans' loading={isLoading}>
          <List
            dataSource={sleepManagement}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  <Button
                    type='text'
                    danger
                    onClick={() => handleDeleteSleep(item.id)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                ]}
              >
                <List.Item.Meta
                  title={item.name}
                  description={`${item.type} - ${item.frequency}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedSleep(item)}
                />
              </List.Item>
            )}
            footer={
              <Button
                type='dashed'
                block
                onClick={() => setSelectedSleep(null)}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add New Sleep & Recovery Plan
              </Button>
            }
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card title={selectedSleep ? 'Edit Sleep & Recovery Plan' : 'Add Sleep & Recovery Plan'}>
          <Form
            form={form}
            layout='vertical'
            onFinish={selectedSleep ? handleEditSleep : handleAddSleep}
            initialValues={selectedSleep || {}}
          >
            <Form.Item name='name' label='Plan Name' rules={[{ required: true, message: 'Please enter a name' }]}>
              <Input placeholder='e.g., Nighttime Routine' />
            </Form.Item>

            <Form.Item name='type' label='Plan Type' rules={[{ required: true, message: 'Please select a plan type' }]}>
              <Select placeholder='Select plan type'>
                {sleepOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='description' label='Description'>
              <Input.TextArea rows={3} placeholder='Describe the sleep or recovery plan...' />
            </Form.Item>

            <Form.Item
              name='duration'
              label='Target Duration (hours)'
              rules={[{ required: true, message: 'Please specify duration' }]}
            >
              <InputNumber min={0.5} max={12} step={0.5} />
            </Form.Item>

            <Form.Item
              name='frequency'
              label='Frequency'
              rules={[{ required: true, message: 'Please select a frequency' }]}
            >
              <Select placeholder='Select frequency'>
                <Option value='daily'>Daily</Option>
                <Option value='weeknights only'>Weeknights Only</Option>
                <Option value='weekends only'>Weekends Only</Option>
                <Option value='as needed'>As Needed</Option>
                <Option value='during high stress periods'>During High Stress Periods</Option>
              </Select>
            </Form.Item>

            <Form.Item name='goals' label='Sleep Goals'>
              <Select mode='multiple' placeholder='Select sleep goals'>
                <Option value='improved-quality'>Improved Sleep Quality</Option>
                <Option value='reduce-insomnia'>Reduce Insomnia</Option>
                <Option value='establish-routine'>Establish Sleep Routine</Option>
                <Option value='reduce-sleep-disturbances'>Reduce Sleep Disturbances</Option>
                <Option value='improve-daytime-energy'>Improve Daytime Energy</Option>
                <Option value='optimize-sleep-duration'>Optimize Sleep Duration</Option>
                <Option value='enhance-recovery'>Enhance Recovery</Option>
              </Select>
            </Form.Item>

            <Form.Item name='recommendations' label='Recommendations'>
              <Select mode='multiple' placeholder='Select recommendations'>
                <Option value='avoid-screens'>Avoid Screens Before Bed</Option>
                <Option value='consistent-schedule'>Maintain Consistent Schedule</Option>
                <Option value='optimize-environment'>Optimize Sleep Environment</Option>
                <Option value='limit-caffeine'>Limit Caffeine After Noon</Option>
                <Option value='evening-routine'>Establish Evening Routine</Option>
                <Option value='morning-routine'>Establish Morning Routine</Option>
                <Option value='relaxation-techniques'>Use Relaxation Techniques</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type='primary' htmlType='submit'>
                  {selectedSleep ? 'Update' : 'Add'} Sleep & Recovery Plan
                </Button>
                {selectedSleep && <Button onClick={() => setSelectedSleep(null)}>Cancel</Button>}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </CustomRow>
  )
}

export default SleepManagement
